import { omit } from 'lodash'
import React, { useContext, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Select, { mergeStyles } from 'react-select'
import { match } from 'ts-pattern'
import { ThemeContext } from '../../context/ThemeProvider'

const SelfExcludingSelectInput = (props: any): JSX.Element => {
  const { watch } = useFormContext()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === 'dark'
  const { control, column, rowsOptions, i } = props
  const formValues = watch()
  const usedColumns = useMemo(() => {
    const rows = omit(formValues, 'upload_name')
    return Object.entries(rows)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        return value.value
      })
  }, [formValues])

  const selectedParentOption = useMemo(() => {
    return match(formValues?.upload_type?.value)
      .with(1, () => 'Dividends')
      .with(2, () => 'Stocks')
      .with(3, () => 'Currency Exchange')
      .otherwise(() => null)
  }, [formValues?.upload_type?.value])

  const options = useMemo(() => {
    return rowsOptions.map((parentOption: any) => {
      if (parentOption.is_array) return parentOption
      return {
        ...parentOption,
        options: parentOption?.options?.map((childOption: any) => {
          return {
            ...childOption,
            isDisabled:
              usedColumns.includes(childOption.value) ||
              !(parentOption.label === selectedParentOption),
          }
        }),
      }
    })
  }, [rowsOptions, selectedParentOption, usedColumns])

  return (
    <Controller
      name={String(column.Header)}
      control={control}
      render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
        <Select
          isClearable={true}
          name={String(column.Header)}
          key={String(column.Header)}
          options={options}
          placeholder={'Do not import'}
          onChange={onChange}
          onBlur={onBlur}
          styles={mergeStyles(styles(isDark))}
        />
      )}
    />
  )
}

const styles = (isDark?: boolean) => ({
  control: (base: any, { hasValue }: any) => ({
    ...base,
    border: 'none',

    boxShadow: 'none',
    backgroundColor: hasValue ? 'rgba(55, 231, 76, 0.3)' : 'hsl(var(--secondary))',
  }),
  menu: (base: any) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    fontWeight: 'bold',
    fontSize: 12,
    zIndex: 9999,
    backgroundColor: isDark ? 'oklch(0.446 0.043 257.281)' : '#FFF',
    color: isDark ? '#FFF' : '#09121F',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? '#FFF' : '#09121F', // Customize text color based on theme
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? '#FFF' : 'rgba(9,18,31,0.5)', // Slightly transparent for readability
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? isDark
        ? 'oklch(0.554 0.046 257.417)'
        : '#E0E0E0'
      : state.isFocused
      ? isDark
        ? 'oklch(0.5 0.05 257.4)'
        : '#F0F0F0'
      : 'transparent',
    'text-decoration': state.isDisabled ? 'line-through' : 'none',
    color:
      isDark && state.isDisabled
        ? 'oklch(0.6 0.05 257.4)'
        : isDark && !state.isDisabled
        ? '#FFF'
        : !isDark && state.isDisabled
        ? '#D0D0D0'
        : '#09121F',
    ':active': {
      backgroundColor: isDark ? 'oklch(0.6 0.05 257.4)' : '#D0D0D0',
    },
  }),
  groupHeading: (base: any) => ({
    ...base,
    color: isDark ? 'oklch(0.869 0.022 252.894)' : 'hsl(--var(foreground))', // Adjust colors for dark/light themes
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: '0.8em',
    padding: '4px 12px',
  }),
})

export default SelfExcludingSelectInput
